import { useMemo, useState } from "react";
import {
  Popover,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useQueries } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useSpaceQuery } from "@/features/space/queries/space-query.ts";
import { useSearchSuggestionsQuery } from "@/features/search/queries/search-query.ts";
import { usePageQuery } from "@/features/page/queries/page-query.ts";
import { getPageById } from "@/features/page/services/page-service.ts";
import {
  buildPublicPageUrl,
  extractHeadingsFromContent,
} from "@/features/page/page.utils.ts";
import type { IPage } from "@/features/page/types/page.types.ts";
import {
  IconChevronRight,
  IconFile,
  IconHash,
  IconLink,
} from "@tabler/icons-react";
import classes from "../cards/cards.module.css";

export interface LinkCascadePickerProps {
  value: string;
  // Fires on every keystroke — used by card fields that bind live URL.
  onChange: (href: string) => void;
  // Fires only on Enter key or picker selection — used by bubble link-menu
  // which should commit only on explicit user action. If omitted, falls
  // back to onChange for commits.
  onCommit?: (href: string) => void;
  // Fires only when the user picks a page (or page heading) from the
  // dropdown. Lets callers capture the page metadata (title, slug, space)
  // for richer rendering — e.g. embed-url shows the page title without
  // hitting /embed/metadata for internal URLs.
  onPickPage?: (page: Partial<IPage>, url: string, anchor?: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  inputClassName?: string;
  inputSize?: "xs" | "sm" | "md";
}

// How many pages from the top search result to hydrate for heading search.
const HEADING_HYDRATE_LIMIT = 5;

type HeadingHit = {
  page: Partial<IPage>;
  heading: { text: string; level: number; anchor: string };
};

export function LinkCascadePicker({
  value,
  onChange,
  onCommit,
  onPickPage,
  placeholder = "Type a URL or search in your content…",
  autoFocus,
  inputClassName,
  inputSize = "xs",
}: LinkCascadePickerProps) {
  const commit = onCommit ?? onChange;
  const { spaceSlug } = useParams();
  const { data: space } = useSpaceQuery(spaceSlug);
  const [query, setQuery] = useState(value);
  const [search, setSearch] = useState("");
  const [col2Search, setCol2Search] = useState("");
  const [open, setOpen] = useState(false);
  const [hoveredPage, setHoveredPage] = useState<Partial<IPage> | null>(null);
  const { data: suggestion } = useSearchSuggestionsQuery({
    query: search,
    includeUsers: false,
    includePages: true,
    spaceId: space?.id,
    limit: 50,
    preload: true,
  });
  const pages: Partial<IPage>[] = suggestion?.pages ?? [];

  // Hydrate top-N pages (by title-match) so we can fuzzy-match their headings
  // against the live query. This is the Option-A fallback while there's no
  // server-side heading index.
  const pagesToHydrate = useMemo(
    () => (search.trim() ? pages.slice(0, HEADING_HYDRATE_LIMIT) : []),
    [pages, search],
  );

  const hydrated = useQueries({
    queries: pagesToHydrate.map((p) => ({
      queryKey: ["pages", p.id],
      queryFn: () => getPageById({ pageId: p.id }),
      enabled: !!p.id,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const headingHits: HeadingHit[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const hits: HeadingHit[] = [];
    for (let i = 0; i < hydrated.length; i++) {
      const full = hydrated[i].data as IPage | undefined;
      if (!full?.content) continue;
      let json: any = full.content;
      if (typeof json === "string") {
        try {
          json = JSON.parse(json);
        } catch {
          continue;
        }
      }
      const hs = extractHeadingsFromContent(json);
      for (const h of hs) {
        if (h.text.toLowerCase().includes(q)) {
          hits.push({ page: pagesToHydrate[i], heading: h });
        }
      }
    }
    return hits.slice(0, 20);
  }, [hydrated, pagesToHydrate, search]);

  const { data: fullPage } = usePageQuery(
    hoveredPage?.id ? { pageId: hoveredPage.id } : { pageId: undefined },
  );

  const headings = useMemo(() => {
    if (!fullPage?.content) return [];
    let json: any = fullPage.content;
    if (typeof json === "string") {
      try {
        json = JSON.parse(json);
      } catch {
        return [];
      }
    }
    return extractHeadingsFromContent(json);
  }, [fullPage?.content]);

  const filteredHeadings = useMemo(() => {
    const q = col2Search.trim().toLowerCase();
    if (!q) return headings;
    return headings.filter((h) => h.text.toLowerCase().includes(q));
  }, [headings, col2Search]);

  const closePicker = () => {
    setOpen(false);
    setHoveredPage(null);
    setCol2Search("");
  };

  const commitUrl = (url: string) => {
    commit(url);
    setQuery(url);
    closePicker();
  };

  const buildPageUrlFor = (page: Partial<IPage>, anchor?: string) => {
    // Prefer the canonical hierarchical urlPath (mirrors the public sidebar
    // tree): a page can live in space A but its admin-tree parent folder may
    // belong to space B with a different slug. urlPath encodes the full
    // chain from the root, so the resulting /docs/<urlPath> matches the
    // sidebar's active leaf and parent expansion.
    const urlPath = (page as any).urlPath as string | null | undefined;
    if (urlPath) {
      return `/docs/${urlPath}${anchor ? `#${anchor}` : ""}`;
    }
    return buildPublicPageUrl(
      page.space?.slug || spaceSlug,
      {
        publicSlug: (page as any).publicSlug,
        slugId: page.slugId,
      },
      anchor,
    );
  };

  const pickPageTop = (page: Partial<IPage>) => {
    const url = buildPageUrlFor(page);
    if (onPickPage) onPickPage(page, url);
    commitUrl(url);
  };

  const pickHeading = (page: Partial<IPage>, anchor: string | null) => {
    const url = buildPageUrlFor(page, anchor ?? undefined);
    if (onPickPage) onPickPage(page, url, anchor ?? undefined);
    commitUrl(url);
  };

  const pageSubtitle = (p: Partial<IPage>) => {
    // Tab name disambiguates pages that share a title across tabs
    // (e.g. "Actions with proxies" in API v1 vs API v2 vs OpenAPI).
    // Without it the subtitle is just the slug, which differs only by
    // a random suffix and is unreadable for picking.
    const slug =
      (p as any).publicSlug || p.slugId || p.space?.slug || spaceSlug || "";
    const tabName = (p as any).tabName as string | undefined;
    return tabName ? `${tabName} · ${slug}` : slug;
  };

  const hasSearch = !!search.trim();

  return (
    <Popover
      opened={open}
      onChange={setOpen}
      position="bottom-start"
      withinPortal
      shadow="md"
      trapFocus={false}
      closeOnClickOutside
      closeOnEscape
      onClose={closePicker}
    >
      <Popover.Target>
        <TextInput
          className={inputClassName ?? classes.fieldRowInput}
          size={inputSize}
          placeholder={placeholder}
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setQuery(v);
            // Live-track for callers that want typing to update their state
            // (card fields store URL live). When onCommit is provided, the
            // outer caller only reacts on Enter / picker pick.
            if (!onCommit) onChange(v);
            setSearch(v);
            setHoveredPage(null);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              closePicker();
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (query.trim()) commitUrl(query.trim());
            }
          }}
        />
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <div className={classes.linkCascade}>
          <div className={classes.linkCascadeCol}>
            {!hasSearch ? (
              <div className={classes.linkCascadeEmpty}>
                Type to search pages and headings.
              </div>
            ) : (
              <ScrollArea.Autosize mah={360} type="auto">
                {headingHits.length > 0 && (
                  <>
                    <div className={classes.linkCascadeSection}>Headings</div>
                    {headingHits.map((hit, i) => (
                      <UnstyledButton
                        key={`hh-${hit.page.id}-${hit.heading.anchor}-${i}`}
                        className={classes.linkCascadeRow}
                        onClick={() =>
                          pickHeading(hit.page, hit.heading.anchor)
                        }
                      >
                        <Text
                          size="10px"
                          c="dimmed"
                          style={{
                            fontFamily: "monospace",
                            flexShrink: 0,
                            minWidth: 18,
                          }}
                        >
                          H{hit.heading.level}
                        </Text>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text size="xs" fw={500} truncate>
                            {hit.heading.text}
                          </Text>
                          <Text size="10px" c="dimmed" truncate>
                            {hit.page.title || "Untitled"} ·{" "}
                            {pageSubtitle(hit.page)}
                          </Text>
                        </div>
                      </UnstyledButton>
                    ))}
                  </>
                )}

                {pages.length === 0 && headingHits.length === 0 ? (
                  <div className={classes.linkCascadeEmpty}>
                    No pages or headings found.
                  </div>
                ) : (
                  pages.length > 0 && (
                    <>
                      <div className={classes.linkCascadeSection}>
                        Pages in {space?.name || "current space"}
                      </div>
                      {pages.map((p) => {
                        const active = hoveredPage?.id === p.id;
                        return (
                          <UnstyledButton
                            key={p.id}
                            className={`${classes.linkCascadeRow} ${
                              active ? classes.linkCascadeRowActive : ""
                            }`}
                            onClick={() => {
                              setHoveredPage(p);
                              setCol2Search("");
                            }}
                          >
                            <IconFile
                              size={14}
                              style={{ flexShrink: 0, opacity: 0.7 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text size="xs" fw={500} truncate>
                                {p.title || "Untitled"}
                              </Text>
                              <Text size="10px" c="dimmed" truncate>
                                {pageSubtitle(p)}
                              </Text>
                            </div>
                            <IconChevronRight
                              size={12}
                              style={{ flexShrink: 0, opacity: 0.5 }}
                            />
                          </UnstyledButton>
                        );
                      })}
                    </>
                  )
                )}
              </ScrollArea.Autosize>
            )}
          </div>

          {hoveredPage && (
            <div className={classes.linkCascadeCol}>
              <div className={classes.linkCascadeHeader}>
                <Text size="xs" fw={600} truncate>
                  {hoveredPage.title || "Untitled"}
                </Text>
              </div>
              <TextInput
                size="xs"
                placeholder="Search heading, tab, or page…"
                value={col2Search}
                onChange={(e) => setCol2Search(e.currentTarget.value)}
                mb={6}
                leftSection={<IconHash size={12} />}
              />
              <ScrollArea.Autosize mah={320} type="auto">
                <UnstyledButton
                  className={classes.linkCascadeRow}
                  onClick={() => pickPageTop(hoveredPage)}
                >
                  <IconLink
                    size={14}
                    style={{ flexShrink: 0, opacity: 0.7 }}
                  />
                  <Text size="xs" fw={500} truncate>
                    Link to {hoveredPage.title || "page"}
                  </Text>
                </UnstyledButton>
                <div className={classes.linkCascadeSection}>Page headings</div>
                {filteredHeadings.length === 0 ? (
                  <div className={classes.linkCascadeEmpty}>
                    {headings.length === 0
                      ? "No headings on this page."
                      : "No headings match."}
                  </div>
                ) : (
                  filteredHeadings.map((h, i) => (
                    <UnstyledButton
                      key={`${h.anchor}-${i}`}
                      className={classes.linkCascadeRow}
                      onClick={() => pickHeading(hoveredPage, h.anchor)}
                      style={{ paddingLeft: 8 + (h.level - 1) * 14 }}
                    >
                      <Text
                        size="10px"
                        c="dimmed"
                        style={{ fontFamily: "monospace", flexShrink: 0 }}
                      >
                        H{h.level}
                      </Text>
                      <Text size="xs" truncate>
                        {h.text}
                      </Text>
                    </UnstyledButton>
                  ))
                )}
              </ScrollArea.Autosize>
            </div>
          )}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

// Back-compat alias for existing imports.
export const LinkFieldInput = LinkCascadePicker;
